import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, map, of, catchError, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';

/** Google Calendar API event resource (subset used by this app). */
export interface GoogleCalendarApiEvent {
  id?: string;
  summary?: string;
  description?: string;
  htmlLink?: string;
  start?: { dateTime?: string; date?: string; timeZone?: string };
  end?: { dateTime?: string; date?: string; timeZone?: string };
  status?: string;
}

interface GoogleEventsListResponse {
  items?: GoogleCalendarApiEvent[];
}

/** Body for creating/updating events (Calendar API v3). */
export interface GoogleCalendarEventPayload {
  summary: string;
  description?: string;
  start: { dateTime: string; timeZone: string } | { date: string };
  end: { dateTime: string; timeZone: string } | { date: string };
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient(config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token?: string; expires_in?: number; error?: string; error_description?: string }) => void;
            error_callback?: (error: { type: string; message?: string }) => void;
          }): { requestAccessToken: (overrideConfig?: { prompt?: '' | 'none' | 'consent' | 'select_account' }) => void };
          revoke: (token: string, done: () => void) => void;
        };
      };
    };
  }
}

const CALENDAR_SCOPE =
  'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events';

const TOKEN_KEY = 'gcal_access_token';
const TOKEN_EXPIRY_KEY = 'gcal_token_expires_at';

@Injectable({ providedIn: 'root' })
export class GoogleCalendarService {
  private readonly http = inject(HttpClient);
  private readonly apiBase = 'https://www.googleapis.com/calendar/v3';

  /** Current OAuth access token (GIS token client). */
  readonly accessToken$ = new BehaviorSubject<string | null>(
    typeof localStorage !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null,
  );

  readonly isSignedIn$ = this.accessToken$.pipe(map((t) => !!t));

  private gsiScriptPromise: Promise<void> | null = null;

  constructor() {
    if (this.accessToken$.value) {
      if (this.isTokenExpired()) {
        this.tryRestoreSession();
      } else {
        this.scheduleProactiveRefresh();
      }
    }
  }

  private ensureAccessToken(): string {
    const t = this.accessToken$.value;
    if (!t) throw new Error('No hay sesión con Google. Conecte su cuenta primero.');
    return t;
  }

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${this.ensureAccessToken()}`,
    });
  }

  /** Identifica el proyecto en Calendar v3 (?key=); mismo GCP que OAuth y Calendar API habilitada. */
  private appendApiKey(params: HttpParams): HttpParams {
    const key = environment.googleCalendarApiKey?.trim();
    return key ? params.set('key', key) : params;
  }

  /** Limpia el token si la API responde 401 (token expirado). */
  private handleAuthError<T>() {
    return (source: Observable<T>) =>
      source.pipe(
        catchError((err) => {
          if (err?.status === 401) {
            this.setToken(null);
          }
          return throwError(() => err);
        }),
      );
  }

  /** Loads Google Identity Services script once. */
  loadGsi(): Promise<void> {
    if (this.gsiScriptPromise) return this.gsiScriptPromise;
    if (typeof window !== 'undefined' && window.google?.accounts?.oauth2) {
      this.gsiScriptPromise = Promise.resolve();
      return this.gsiScriptPromise;
    }
    this.gsiScriptPromise = new Promise((resolve, reject) => {
      const id = 'google-gsi-client';
      if (document.getElementById(id)) {
        const deadline = Date.now() + 15000;
        const tick = (): void => {
          if (window.google?.accounts?.oauth2) {
            resolve();
            return;
          }
          if (Date.now() > deadline) {
            reject(new Error('Timeout cargando GSI'));
            return;
          }
          setTimeout(tick, 50);
        };
        tick();
        return;
      }
      const script = document.createElement('script');
      script.id = id;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('No se pudo cargar Google Identity Services'));
      document.head.appendChild(script);
    });
    return this.gsiScriptPromise;
  }

  /**
   * Requests an access token via GIS token client (popup/consent as needed).
   */
  async signIn(): Promise<void> {
    const clientId = environment.googleClientId?.trim();
    if (!clientId) {
      throw new Error('Configure googleClientId en environment.');
    }
    await this.loadGsi();
    const oauth2 = window.google?.accounts?.oauth2;
    if (!oauth2) throw new Error('Google OAuth2 no está disponible');

    return new Promise((resolve, reject) => {
      const client = oauth2.initTokenClient({
        client_id: clientId,
        scope: CALENDAR_SCOPE,
        callback: (resp) => {
          if (resp.error) {
            reject(new Error(resp.error_description ?? resp.error));
            return;
          }
          if (resp.access_token) {
            this.setToken(resp.access_token, resp.expires_in);
            resolve();
          } else {
            reject(new Error('No se recibió access_token'));
          }
        },
        error_callback: (err) => {
          reject(new Error(err.message ?? 'Inicio de sesión cancelado'));
        },
      });
      // Si ya se autorizó antes, omitir el selector de cuenta
      const hasExisting = !!this.accessToken$.value;
      client.requestAccessToken(hasExisting ? { prompt: '' } : undefined);
    });
  }

  private setToken(token: string | null, expiresIn?: number): void {
    this.accessToken$.next(token);
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      if (expiresIn) {
        const expiresAt = Date.now() + expiresIn * 1000;
        localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiresAt));
      }
      this.scheduleProactiveRefresh();
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
      this.clearRefreshTimer();
    }
  }

  private isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiresAt) return true;
    return Date.now() >= Number(expiresAt);
  }

  /** Intenta re-autenticación silenciosa (sin popup) con GIS. */
  private async tryRestoreSession(): Promise<void> {
    try {
      await this.loadGsi();
      const oauth2 = window.google?.accounts?.oauth2;
      const clientId = environment.googleClientId?.trim();
      if (!oauth2 || !clientId) {
        this.setToken(null);
        return;
      }
      await new Promise<void>((resolve, reject) => {
        const client = oauth2.initTokenClient({
          client_id: clientId,
          scope: CALENDAR_SCOPE,
          callback: (resp) => {
            if (resp.error || !resp.access_token) {
              reject(new Error(resp.error_description ?? resp.error ?? 'No token'));
              return;
            }
            if (this.accessToken$.value !== null) {
              this.setToken(resp.access_token, resp.expires_in);
            }
            resolve();
          },
          error_callback: (err) => {
            reject(new Error(err.message ?? 'Silent auth failed'));
          },
        });
        client.requestAccessToken({ prompt: '' });
      });
    } catch {
      this.setToken(null);
    }
  }

  private refreshTimer: ReturnType<typeof setTimeout> | null = null;

  private scheduleProactiveRefresh(): void {
    this.clearRefreshTimer();
    const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiresAt) return;
    const msUntilRefresh = Number(expiresAt) - Date.now() - 5 * 60 * 1000;
    if (msUntilRefresh <= 0) {
      this.tryRestoreSession();
      return;
    }
    this.refreshTimer = setTimeout(() => this.tryRestoreSession(), msUntilRefresh);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /** Clears token and revokes it with Google when possible. */
  signOut(): void {
    this.clearRefreshTimer();
    const token = this.accessToken$.value;
    this.setToken(null);
    if (token && window.google?.accounts?.oauth2?.revoke) {
      try {
        window.google.accounts.oauth2.revoke(token, () => undefined);
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Lists events in a time range (single events expanded).
   */
  getEvents(calendarId: string, timeMin: Date, timeMax: Date): Observable<GoogleCalendarApiEvent[]> {
    if (!this.accessToken$.value) {
      return of([]);
    }
    const encoded = encodeURIComponent(calendarId);
    const params = this.appendApiKey(
      new HttpParams()
        .set('timeMin', timeMin.toISOString())
        .set('timeMax', timeMax.toISOString())
        .set('singleEvents', 'true')
        .set('orderBy', 'startTime'),
    );
    return this.http
      .get<GoogleEventsListResponse>(`${this.apiBase}/calendars/${encoded}/events`, {
        headers: this.authHeaders(),
        params,
      })
      .pipe(map((r) => r.items ?? []), this.handleAuthError());
  }

  createEvent(calendarId: string, body: GoogleCalendarEventPayload): Observable<GoogleCalendarApiEvent> {
    const encoded = encodeURIComponent(calendarId);
    return this.http
      .post<GoogleCalendarApiEvent>(`${this.apiBase}/calendars/${encoded}/events`, body, {
        headers: this.authHeaders(),
        params: this.appendApiKey(new HttpParams()),
      })
      .pipe(this.handleAuthError());
  }

  updateEvent(
    calendarId: string,
    eventId: string,
    body: Partial<GoogleCalendarEventPayload>,
  ): Observable<GoogleCalendarApiEvent> {
    const encCal = encodeURIComponent(calendarId);
    const encEv = encodeURIComponent(eventId);
    return this.http
      .patch<GoogleCalendarApiEvent>(
        `${this.apiBase}/calendars/${encCal}/events/${encEv}`,
        body,
        { headers: this.authHeaders(), params: this.appendApiKey(new HttpParams()) },
      )
      .pipe(this.handleAuthError());
  }

  deleteEvent(calendarId: string, eventId: string): Observable<void> {
    const encCal = encodeURIComponent(calendarId);
    const encEv = encodeURIComponent(eventId);
    return this.http
      .delete(`${this.apiBase}/calendars/${encCal}/events/${encEv}`, {
        headers: this.authHeaders(),
        params: this.appendApiKey(new HttpParams()),
      })
      .pipe(map(() => undefined), this.handleAuthError());
  }

  /** IANA timezone for API payloads (browser). */
  getUserTimeZone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }
}
